<template>
  <div class="min-h-screen bg-gradient-to-br from-amber-900/20 via-amber-800/10 to-amber-900/20 p-6">
    <div class="max-w-7xl mx-auto">
      <!-- Header -->
      <div class="mb-8">
        <h1 class="text-4xl font-bold text-amber-200 mb-2">Messagerie</h1>
        <p class="text-amber-300/70">Communiquez avec les autres joueurs</p>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- Liste des conversations -->
        <div class="lg:col-span-1">
          <div class="bg-amber-900/30 backdrop-blur-sm rounded-xl border border-amber-700/40 p-6">
            <div class="flex items-center justify-between mb-4">
              <h2 class="text-xl font-semibold text-amber-200">Conversations</h2>
              <button 
                @click="openNewMessageModal"
                class="px-4 py-2 bg-gradient-to-r from-amber-600/80 to-amber-800/80 text-amber-100 rounded-lg hover:from-amber-600/90 hover:to-amber-800/90 transition-all duration-300 border border-amber-700/40"
              >
                Nouveau message
              </button>
            </div>

            <!-- Recherche d'utilisateurs -->
            <div class="mb-4">
              <input
                v-model="searchUser"
                type="text"
                placeholder="Rechercher un utilisateur..."
                class="w-full px-4 py-2 bg-amber-800/40 border border-amber-700/40 rounded-lg text-amber-200 placeholder-amber-400/60 focus:outline-none focus:ring-2 focus:ring-amber-600/50"
                @input="searchUsers"
              />
            </div>

            <!-- Liste des utilisateurs trouvés -->
            <div v-if="searchResults.length > 0" class="mb-4">
              <div class="text-sm text-amber-300/70 mb-2">Résultats de recherche :</div>
              <div class="space-y-2">
                <div
                  v-for="user in searchResults"
                  :key="user.id"
                  @click="startConversation(user.username)"
                  class="p-3 bg-amber-800/40 rounded-lg cursor-pointer hover:bg-amber-700/40 transition-colors"
                >
                  <div class="font-medium text-amber-200">{{ user.username }}</div>
                  <div class="text-sm text-amber-300/70">{{ user.email }}</div>
                </div>
              </div>
            </div>

            <!-- Liste des conversations -->
            <div class="space-y-2">
              <div v-if="conversations.length === 0" class="text-center text-amber-400/70 text-sm py-4">
                Aucune conversation
              </div>
              <div
                v-for="conversation in conversations"
                :key="conversation.id"
                @click="selectConversation(conversation)"
                :class="[
                  'p-4 rounded-lg cursor-pointer transition-all duration-200',
                  selectedConversation && selectedConversation.id === conversation.id
                    ? 'bg-amber-700/50 border border-amber-600/60 shadow-lg'
                    : 'bg-amber-800/40 hover:bg-amber-700/40 border border-transparent'
                ]"
              >
                <div class="flex items-start justify-between gap-3">
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 mb-1">
                      <div class="font-semibold text-amber-200 text-base">
                        {{ getOtherUsername(conversation) }}
                      </div>
                      <div v-if="!conversation.isRead && conversation.receiverUsername === currentUser?.username" 
                           class="w-2 h-2 bg-amber-500 rounded-full flex-shrink-0"></div>
                    </div>
                    <div class="text-sm text-amber-300/80 leading-relaxed line-clamp-2">
                      {{ conversation.content }}
                    </div>
                  </div>
                  <div class="text-right flex-shrink-0">
                    <div class="text-xs text-amber-400/70 mb-1">{{ formatDate(conversation.createdAt) }}</div>
                    <div v-if="!conversation.isRead && conversation.receiverUsername === currentUser?.username" 
                         class="text-xs text-amber-500 font-medium">Non lu</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Zone de conversation -->
        <div class="lg:col-span-2">
          <div v-if="selectedConversation" class="bg-amber-900/30 backdrop-blur-sm rounded-xl border border-amber-700/40 h-[600px] flex flex-col">
            <!-- Header de la conversation -->
            <div class="p-4 border-b border-amber-700/40">
              <div class="flex items-center justify-between">
                <div>
                  <h3 class="text-lg font-semibold text-amber-200">
                    {{ getOtherUsername(selectedConversation) }}
                  </h3>
                </div>
                <button
                  @click="markConversationAsRead"
                  class="px-3 py-1 bg-amber-700/40 text-amber-200 rounded-lg hover:bg-amber-600/40 transition-colors text-sm"
                >
                  Marquer comme lu
                </button>
              </div>
            </div>

            <!-- Messages -->
            <div class="flex-1 overflow-y-auto p-4 space-y-3">
              <div v-if="messages.length === 0" class="text-center text-amber-400/70 text-sm py-8">
                Aucun message dans cette conversation
              </div>
              <div
                v-for="message in messages"
                :key="message.id"
                :class="[
                  'flex group',
                  message.senderUsername === currentUser?.username ? 'justify-end' : 'justify-start'
                ]"
              >
                <div class="relative">
                  <div
                    :class="[
                      'max-w-xs lg:max-w-md px-4 py-3 rounded-2xl shadow-sm relative',
                      message.senderUsername === currentUser?.username
                        ? 'bg-gradient-to-r from-amber-600/90 to-amber-700/90 text-amber-100 rounded-br-md'
                        : 'bg-amber-800/50 text-amber-200 rounded-bl-md'
                    ]"
                    @contextmenu.prevent="showMessageOptions(message, $event)"
                  >
                    <!-- Contenu du message -->
                    <div class="text-sm leading-relaxed break-words">
                      <div class="whitespace-pre-wrap" v-html="formatMessageContent(message.content)"></div>
                    </div>

                    <!-- Réactions -->
                    <div v-if="message.reactions && message.reactions.length > 0" class="flex flex-wrap gap-1 mt-2">
                      <div
                        v-for="reaction in message.reactions"
                        :key="reaction.emoji"
                        class="flex items-center space-x-1 px-2 py-1 bg-amber-800/40 rounded-full text-xs reaction-bubble cursor-pointer"
                        @click="toggleReaction(message, reaction.emoji)"
                      >
                        <span>{{ reaction.emoji }}</span>
                        <span class="text-amber-300">{{ reaction.count }}</span>
                      </div>
                    </div>

                    <!-- Footer du message -->
                    <div class="flex items-center justify-between mt-2 text-xs opacity-75">
                      <div class="flex items-center space-x-2">
                        <span>{{ formatTime(message.createdAt) }}</span>
                        <span v-if="message.senderUsername === currentUser?.username">
                          <span v-if="message.isRead" class="text-amber-300">✓✓</span>
                          <span v-else class="text-amber-400">✓</span>
                        </span>
                      </div>
                      
                      <!-- Bouton de réaction (visible au survol) -->
                      <button
                        v-if="message.senderUsername !== currentUser?.username"
                        @click="showReactionPicker(message)"
                        class="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-amber-700/40 rounded"
                        title="Réagir"
                      >
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                      </button>
                    </div>
                  </div>

                  <!-- Menu contextuel des options de message -->
                  <div
                    v-if="messageOptions.message && messageOptions.message.id === message.id"
                    :style="{ left: messageOptions.x + 'px', top: messageOptions.y + 'px' }"
                    class="absolute z-20 bg-amber-900/95 backdrop-blur-sm rounded-lg border border-amber-700/40 p-2 shadow-xl context-menu"
                  >
                    <button
                      @click="copyMessage(message)"
                      class="w-full text-left px-3 py-2 text-sm text-amber-200 hover:bg-amber-700/40 rounded transition-colors"
                    >
                      📋 Copier
                    </button>
                    <button
                      v-if="message.senderUsername === currentUser?.username"
                      @click="deleteMessage(message)"
                      class="w-full text-left px-3 py-2 text-sm text-red-400 hover:bg-red-900/40 rounded transition-colors"
                    >
                      🗑️ Supprimer
                    </button>
                    <button
                      @click="replyToMessage(message)"
                      class="w-full text-left px-3 py-2 text-sm text-amber-200 hover:bg-amber-700/40 rounded transition-colors"
                    >
                      ↩️ Répondre
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Zone de saisie -->
            <div class="p-4 border-t border-amber-700/40 bg-amber-900/20">
              <!-- Indicateur de frappe -->
              <div v-if="isTyping" class="text-xs text-amber-400/70 mb-2 flex items-center">
                <div class="flex space-x-1 mr-2">
                  <div class="w-1 h-1 bg-amber-400 rounded-full animate-bounce"></div>
                  <div class="w-1 h-1 bg-amber-400 rounded-full animate-bounce" style="animation-delay: 0.1s"></div>
                  <div class="w-1 h-1 bg-amber-400 rounded-full animate-bounce" style="animation-delay: 0.2s"></div>
                </div>
                {{ typingUser }} est en train d'écrire...
              </div>

              <div class="flex space-x-3">
                <!-- Bouton emoji -->
                <button
                  @click="toggleEmojiPicker"
                  class="px-3 py-3 bg-amber-800/40 text-amber-300 rounded-xl hover:bg-amber-700/40 transition-colors border border-amber-700/40"
                  title="Ajouter un emoji"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </button>

                <!-- Bouton formatage de texte -->
                <div class="flex space-x-1">
                  <button
                    @click="toggleBold"
                    :class="[
                      'px-3 py-3 rounded-xl transition-colors border border-amber-700/40 formatting-button',
                      textFormatting.bold 
                        ? 'bg-amber-600/80 text-amber-100 active' 
                        : 'bg-amber-800/40 text-amber-300 hover:bg-amber-700/40'
                    ]"
                    title="Gras"
                  >
                    <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M15.6 10.79c.97-.67 1.65-1.77 1.65-2.79 0-2.26-1.75-4-4-4H7v14h7.04c2.09 0 3.71-1.7 3.71-3.79 0-1.52-.86-2.82-2.15-3.42zM10 6.5h3c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-3v-3zm3.5 9H10v-3h3.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5z"/>
                    </svg>
                  </button>
                  
                  <button
                    @click="toggleItalic"
                    :class="[
                      'px-3 py-3 rounded-xl transition-colors border border-amber-700/40 formatting-button',
                      textFormatting.italic 
                        ? 'bg-amber-600/80 text-amber-100 active' 
                        : 'bg-amber-800/40 text-amber-300 hover:bg-amber-700/40'
                    ]"
                    title="Italique"
                  >
                    <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M10 4v3h2.21l-3.42 8H6v3h8v-3h-2.21l3.42-8H18V4h-8z"/>
                    </svg>
                  </button>
                  
                </div>

                <!-- Input de message -->
                <div class="flex-1 relative">
                  <textarea
                    ref="messageInput"
                    v-model="newMessage"
                    @keydown="handleTextareaKeyDown"
                    @input="handleTyping"
                    placeholder="Tapez votre message... (Utilisez **gras** et *italique*)"
                    class="w-full px-4 py-3 bg-amber-800/50 border border-amber-700/50 rounded-xl text-amber-200 placeholder-amber-400/60 focus:outline-none focus:ring-2 focus:ring-amber-600/50 focus:border-amber-600/50 transition-all duration-200 resize-none"
                    rows="1"
                    style="min-height: 48px; max-height: 120px;"
                  ></textarea>
                  
                  <!-- Sélecteur d'emojis -->
                  <div v-if="showEmojiPicker" class="absolute bottom-full left-0 mb-2 bg-amber-900/95 backdrop-blur-sm rounded-xl border border-amber-700/40 p-4 shadow-xl z-10 emoji-picker">
                    <div class="grid grid-cols-8 gap-2 max-h-48 overflow-y-auto emoji-grid">
                      <button
                        v-for="emoji in popularEmojis"
                        :key="emoji"
                        @click="addEmoji(emoji)"
                        class="p-2 text-lg hover:bg-amber-700/40 rounded-lg transition-colors"
                        :title="emoji"
                      >
                        {{ emoji }}
                      </button>
                    </div>
                    <div class="text-xs text-amber-400/70 mt-2 text-center">Emojis populaires</div>
                  </div>
                </div>


                <!-- Bouton d'envoi -->
                <button
                  @click="sendMessage"
                  :disabled="!newMessage.trim()"
                  class="px-6 py-3 bg-gradient-to-r from-amber-600/90 to-amber-700/90 text-amber-100 rounded-xl hover:from-amber-600 hover:to-amber-700 transition-all duration-200 border border-amber-600/50 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <!-- Message si aucune conversation sélectionnée -->
          <div v-else class="bg-amber-900/30 backdrop-blur-sm rounded-xl border border-amber-700/40 h-[600px] flex items-center justify-center">
            <div class="text-center">
              <div class="text-6xl text-amber-400/40 mb-4">💬</div>
              <h3 class="text-xl font-semibold text-amber-200 mb-2">Sélectionnez une conversation</h3>
              <p class="text-amber-300/70">Choisissez une conversation ou créez un nouveau message</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal nouveau message -->
    <div v-if="showNewMessageModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div class="bg-amber-900/90 backdrop-blur-sm rounded-xl border border-amber-700/40 p-6 w-full max-w-md">
        <h3 class="text-xl font-semibold text-amber-200 mb-4">Nouveau message</h3>
        
        <div class="mb-4">
          <label class="block text-sm font-medium text-amber-300 mb-2">Destinataire</label>
          <select
            v-model="newMessageReceiver"
            :disabled="loadingUsers"
            class="w-full px-4 py-2 bg-amber-800/40 border border-amber-700/40 rounded-lg text-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-600/50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="" disabled>
              {{ loadingUsers ? 'Chargement des utilisateurs...' : 'Sélectionnez un utilisateur' }}
            </option>
            <option 
              v-for="user in validUsers" 
              :key="user.id" 
              :value="user.username"
              class="bg-amber-800 text-amber-200"
            >
              {{ user.username }}
            </option>
          </select>
          <div v-if="validUsers.length === 0 && !loadingUsers" class="text-sm text-amber-400/70 mt-1">
            Aucun utilisateur disponible
          </div>
        </div>

        <div class="mb-6">
          <label class="block text-sm font-medium text-amber-300 mb-2">Message</label>
          <textarea
            v-model="newMessageContent"
            rows="4"
            placeholder="Votre message..."
            class="w-full px-4 py-2 bg-amber-800/40 border border-amber-700/40 rounded-lg text-amber-200 placeholder-amber-400/60 focus:outline-none focus:ring-2 focus:ring-amber-600/50 resize-none"
          ></textarea>
        </div>

        <div class="flex space-x-3">
          <button
            @click="sendNewMessage"
            :disabled="!newMessageReceiver.trim() || !newMessageContent.trim()"
            class="flex-1 px-4 py-2 bg-gradient-to-r from-amber-600/80 to-amber-800/80 text-amber-100 rounded-lg hover:from-amber-600/90 hover:to-amber-800/90 transition-all duration-300 border border-amber-700/40 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Envoyer
          </button>
          <button
            @click="showNewMessageModal = false"
            class="px-4 py-2 bg-amber-800/40 text-amber-300 rounded-lg hover:bg-amber-700/40 transition-colors"
          >
            Annuler
          </button>
        </div>
      </div>
    </div>


    <!-- Modal de sélection de réaction -->
    <div v-if="showReactionModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 modal-backdrop" @click="closeReactionModal">
      <div class="bg-amber-900/95 backdrop-blur-sm rounded-xl border border-amber-700/40 p-4 shadow-xl" @click.stop>
        <h3 class="text-lg font-semibold text-amber-200 mb-4">Réagir au message</h3>
        <div class="grid grid-cols-6 gap-3">
          <button
            v-for="emoji in reactionEmojis"
            :key="emoji"
            @click="addReaction(emoji)"
            class="p-3 text-2xl hover:bg-amber-700/40 rounded-lg transition-colors"
            :title="emoji"
          >
            {{ emoji }}
          </button>
        </div>
        <div class="flex justify-end mt-4">
          <button
            @click="closeReactionModal"
            class="px-4 py-2 bg-amber-800/40 text-amber-300 rounded-lg hover:bg-amber-700/40 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { useStore } from 'vuex';
import MessageService from '@/services/message.service';
import UserService from '@/services/user.service';

export default {
  name: 'MessagesView',
  setup() {
    const store = useStore();
    const currentUser = computed(() => store.getters['auth/user']);

    // État réactif
    const conversations = ref([]);
    const messages = ref([]);
    const selectedConversation = ref(null);
    const newMessage = ref('');
    const searchUser = ref('');
    const searchResults = ref([]);
    const showNewMessageModal = ref(false);
    const newMessageReceiver = ref('');
    const newMessageContent = ref('');
    const loading = ref(false);
    const loadingUsers = ref(false);
    const availableUsers = ref([]);

    // Nouvelles fonctionnalités de messagerie
    const showEmojiPicker = ref(false);
    const isTyping = ref(false);
    const typingUser = ref('');
    const showImageModal = ref(false);
    const selectedImage = ref('');
    const showReactionModal = ref(false);
    const selectedMessageForReaction = ref(null);
    const messageOptions = ref({ message: null, x: 0, y: 0 });
    const replyTo = ref(null);

    // Formatage de texte
    const textFormatting = ref({
      bold: false,
      italic: false
    });

    // Emojis populaires
    const popularEmojis = ref([
      '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰',
      '😘', '😗', '😙', '😚', '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🤩', '🥳', '😏',
      '😒', '😞', '😔', '😟', '😕', '🙁', '☹️', '😣', '😖', '😫', '😩', '🥺', '😢', '😭', '😤', '😠',
      '😡', '🤬', '🤯', '😳', '🥵', '🥶', '😱', '😨', '😰', '😥', '😓', '🤗', '🤔', '🤭', '🤫', '🤥',
      '👍', '👎', '👌', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👋', '🤚',
      '🖐️', '✋', '🖖', '👏', '🙌', '👐', '🤲', '🤝', '🙏', '✍️', '💅', '🤳', '💪', '🦾', '🦿', '🦵'
    ]);

    // Emojis de réaction
    const reactionEmojis = ref(['👍', '👎', '❤️', '😂', '😮', '😢', '😡', '🎉', '🔥', '💯', '👏', '🙌']);

    // Variables pour la frappe
    let typingTimeout = null;
    let lastTypingTime = 0;


    // Propriété calculée pour filtrer les utilisateurs valides
    const validUsers = computed(() => {
      return availableUsers.value.filter(user => 
        user && user.username && user.email
      );
    });

    // Fonction utilitaire pour obtenir le nom de l'autre utilisateur
    const getOtherUsername = (conversation) => {
      if (!conversation || !currentUser.value) return '';
      return conversation.senderUsername === currentUser.value.username 
        ? conversation.receiverUsername 
        : conversation.senderUsername;
    };

    // Charger les conversations
    const loadConversations = async () => {
      try {
        loading.value = true;
        console.log('=== CHARGEMENT CONVERSATIONS ===');
        const response = await MessageService.getConversations();
        console.log('Réponse API conversations:', response.data);
        conversations.value = response.data || [];
        console.log('Conversations chargées:', conversations.value.length);
        
        // Afficher les détails des conversations pour debug
        conversations.value.forEach((conv, index) => {
          console.log(`Conversation ${index}:`, {
            id: conv.id,
            sender: conv.senderUsername,
            receiver: conv.receiverUsername,
            content: conv.content,
            createdAt: conv.createdAt
          });
        });
        
      } catch (error) {
        console.error('Erreur lors du chargement des conversations:', error);
        console.error('Détails erreur:', error.response?.data);
        conversations.value = [];
      } finally {
        loading.value = false;
      }
    };

    // Charger la liste des utilisateurs disponibles
    const loadAvailableUsers = async () => {
      try {
        loadingUsers.value = true;
        const response = await UserService.getAllUsers();
        availableUsers.value = response.data.filter(user => 
          user && 
          user.username && 
          currentUser.value && 
          user.username !== currentUser.value.username
        );
      } catch (error) {
        console.error('Erreur lors du chargement des utilisateurs:', error);
        availableUsers.value = [];
      } finally {
        loadingUsers.value = false;
      }
    };

    // Rechercher des utilisateurs
    const searchUsers = async () => {
      if (searchUser.value.length < 2) {
        searchResults.value = [];
        return;
      }

      try {
        const response = await UserService.getAllUsers();
        const allUsers = response.data;
        searchResults.value = allUsers.filter(user => 
          user && 
          user.username && 
          currentUser.value &&
          user.username.toLowerCase().includes(searchUser.value.toLowerCase()) &&
          user.username !== currentUser.value.username
        );
      } catch (error) {
        console.error('Erreur lors de la recherche d\'utilisateurs:', error);
        searchResults.value = [];
      }
    };

    // Sélectionner une conversation
    const selectConversation = async (conversation) => {
      if (!conversation || !currentUser.value) {
        return;
      }
      
      console.log('=== SÉLECTION CONVERSATION ===');
      console.log('Conversation sélectionnée:', conversation);
      selectedConversation.value = conversation;
      
      // Si c'est une conversation temporaire, ne pas charger les messages du serveur
      if (conversation.isTemporary) {
        console.log('Conversation temporaire, pas de chargement des messages');
        messages.value = [];
        return;
      }
      
      const otherUsername = getOtherUsername(conversation);
      console.log('Chargement des messages avec:', otherUsername);
      
      try {
        const response = await MessageService.getConversation(otherUsername);
        console.log('Messages chargés depuis la DB:', response.data);
        messages.value = response.data || [];
        console.log('Nombre de messages affichés:', messages.value.length);
      } catch (error) {
        console.error('Erreur lors du chargement des messages:', error);
        console.error('Détails erreur:', error.response?.data);
        messages.value = [];
      }
    };

    // Démarrer une nouvelle conversation
    const startConversation = async (username) => {
      if (!currentUser.value) return;
      
      searchUser.value = '';
      searchResults.value = [];
      
      // Vérifier si une conversation existe déjà
      const existingConversation = conversations.value.find(conv => 
        (conv.senderUsername === currentUser.value.username && conv.receiverUsername === username) ||
        (conv.receiverUsername === currentUser.value.username && conv.senderUsername === username)
      );
      
      if (existingConversation) {
        selectConversation(existingConversation);
      } else {
        // Créer une conversation temporaire pour l'affichage
        const newConv = {
          id: `temp_${Date.now()}`,
          senderUsername: currentUser.value.username,
          receiverUsername: username,
          content: 'Nouvelle conversation',
          createdAt: new Date().toISOString(),
          isRead: true,
          isTemporary: true
        };
        conversations.value.unshift(newConv);
        selectConversation(newConv);
      }
    };

    // Envoyer un message
    const sendMessage = async () => {
      if (!newMessage.value.trim() || !selectedConversation.value || !currentUser.value) {
        return;
      }

      const otherUsername = getOtherUsername(selectedConversation.value);

      try {
        const response = await MessageService.sendMessage(otherUsername, newMessage.value);
        
        // Ajouter le message à la liste locale IMMÉDIATEMENT
        messages.value.push(response.data);
        newMessage.value = '';
        
        // Si c'était une conversation temporaire, recharger les conversations
        if (selectedConversation.value.isTemporary) {
          await loadConversations();
          
          // Trouver la vraie conversation créée
          const newConversation = conversations.value.find(conv => 
            (conv.senderUsername === currentUser.value.username && conv.receiverUsername === otherUsername) ||
            (conv.receiverUsername === currentUser.value.username && conv.senderUsername === otherUsername)
          );
          
          if (newConversation) {
            // Ne pas recharger les messages, on les a déjà
            selectedConversation.value = newConversation;
          }
        } else {
          // Mettre à jour la conversation existante
          const convIndex = conversations.value.findIndex(conv => conv.id === selectedConversation.value.id);
          if (convIndex !== -1) {
            conversations.value[convIndex].content = response.data.content;
            conversations.value[convIndex].createdAt = response.data.createdAt;
          }
        }
      } catch (error) {
        console.error('Erreur lors de l\'envoi du message:', error);
        alert('Erreur lors de l\'envoi du message: ' + (error.response?.data?.message || error.message));
      }
    };

    // Envoyer un nouveau message
    const sendNewMessage = async () => {
      if (!newMessageReceiver.value.trim() || !newMessageContent.value.trim() || !currentUser.value) return;

      const receiverUsername = newMessageReceiver.value;
      const messageContent = newMessageContent.value;

      try {
        const response = await MessageService.sendMessage(receiverUsername, messageContent);
        
        // Fermer le modal
        showNewMessageModal.value = false;
        newMessageReceiver.value = '';
        newMessageContent.value = '';
        
        // Recharger les conversations
        await loadConversations();
        
        // Trouver et sélectionner la nouvelle conversation
        const newConversation = conversations.value.find(conv => 
          (conv.senderUsername === currentUser.value.username && conv.receiverUsername === receiverUsername) ||
          (conv.receiverUsername === currentUser.value.username && conv.senderUsername === receiverUsername)
        );
        
        if (newConversation) {
          await selectConversation(newConversation);
        } else {
          // Si aucune conversation trouvée, créer une conversation temporaire
          const tempConv = {
            id: `temp_${Date.now()}`,
            senderUsername: currentUser.value.username,
            receiverUsername: receiverUsername,
            content: messageContent,
            createdAt: response.data.createdAt,
            isRead: true,
            isTemporary: true
          };
          conversations.value.unshift(tempConv);
          selectedConversation.value = tempConv;
          messages.value = [response.data];
        }
        
      } catch (error) {
        console.error('Erreur lors de l\'envoi du message:', error);
        alert('Erreur lors de l\'envoi du message: ' + (error.response?.data?.message || error.message));
      }
    };

    // Ouvrir le modal de nouveau message
    const openNewMessageModal = () => {
      showNewMessageModal.value = true;
      newMessageReceiver.value = '';
      newMessageContent.value = '';
      loadAvailableUsers();
    };

    // Marquer la conversation comme lue
    const markConversationAsRead = async () => {
      if (!selectedConversation.value || !currentUser.value) return;

      const otherUsername = getOtherUsername(selectedConversation.value);

      try {
        console.log('=== MARQUAGE CONVERSATION COMME LUE ===');
        console.log('Conversation avec:', otherUsername);
        
        await MessageService.markConversationAsRead(otherUsername);
        
        // Marquer tous les messages de la conversation comme lus localement
        messages.value.forEach(message => {
          if (message.receiverUsername === currentUser.value.username) {
            message.isRead = true;
          }
        });
        
        // Marquer la conversation comme lue
        selectedConversation.value.isRead = true;
        
        // Recharger les conversations pour mettre à jour l'état
        await loadConversations();
        
        // Émettre un événement pour mettre à jour le compteur de messages non lus
        window.dispatchEvent(new CustomEvent('messagesRead'));
        
        console.log('Conversation marquée comme lue avec succès');
      } catch (error) {
        console.error('Erreur lors du marquage comme lu:', error);
      }
    };

    // Nouvelles fonctions pour les emojis et réactions
    const toggleEmojiPicker = () => {
      showEmojiPicker.value = !showEmojiPicker.value;
    };

    const addEmoji = (emoji) => {
      newMessage.value += emoji;
      showEmojiPicker.value = false;
    };

    const handleTyping = () => {
      const now = Date.now();
      if (now - lastTypingTime > 1000) { // Éviter le spam
        lastTypingTime = now;
        // Ici on pourrait envoyer un signal de frappe au serveur
        // Pour l'instant, on simule juste l'indicateur local
        if (selectedConversation.value) {
          isTyping.value = true;
          typingUser.value = currentUser.value?.username || 'Vous';
          
          if (typingTimeout) {
            clearTimeout(typingTimeout);
          }
          typingTimeout = setTimeout(() => {
            isTyping.value = false;
            typingUser.value = '';
          }, 2000);
        }
      }
      
      // Auto-resize du textarea
      autoResizeTextarea();
    };

    const autoResizeTextarea = () => {
      const textarea = document.querySelector('textarea');
      if (textarea) {
        textarea.style.height = 'auto';
        const newHeight = Math.min(textarea.scrollHeight, 120); // Max 120px
        textarea.style.height = newHeight + 'px';
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        showEmojiPicker.value = false;
        messageOptions.value = { message: null, x: 0, y: 0 };
      }
    };

    const handleTextareaKeyDown = (event) => {
      if (event.key === 'Escape') {
        showEmojiPicker.value = false;
        messageOptions.value = { message: null, x: 0, y: 0 };
      } else if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        sendMessage();
      }
    };


    const openImageModal = (imageSrc) => {
      selectedImage.value = imageSrc;
      showImageModal.value = true;
    };

    const closeImageModal = () => {
      showImageModal.value = false;
      selectedImage.value = '';
    };

    const showReactionPicker = (message) => {
      selectedMessageForReaction.value = message;
      showReactionModal.value = true;
    };

    const closeReactionModal = () => {
      showReactionModal.value = false;
      selectedMessageForReaction.value = null;
    };

    const addReaction = (emoji) => {
      if (!selectedMessageForReaction.value) return;
      
      const message = selectedMessageForReaction.value;
      if (!message.reactions) {
        message.reactions = [];
      }
      
      const existingReaction = message.reactions.find(r => r.emoji === emoji);
      if (existingReaction) {
        existingReaction.count++;
      } else {
        message.reactions.push({ emoji, count: 1 });
      }
      
      closeReactionModal();
    };

    const toggleReaction = (message, emoji) => {
      if (!message.reactions) {
        message.reactions = [];
      }
      
      const existingReaction = message.reactions.find(r => r.emoji === emoji);
      if (existingReaction) {
        existingReaction.count--;
        if (existingReaction.count <= 0) {
          message.reactions = message.reactions.filter(r => r.emoji !== emoji);
        }
      }
    };

    const showMessageOptions = (message, event) => {
      messageOptions.value = {
        message,
        x: event.clientX,
        y: event.clientY
      };
    };

    const copyMessage = (message) => {
      navigator.clipboard.writeText(message.content);
      messageOptions.value = { message: null, x: 0, y: 0 };
    };

    const deleteMessage = (message) => {
      if (confirm('Êtes-vous sûr de vouloir supprimer ce message ?')) {
        const index = messages.value.findIndex(m => m.id === message.id);
        if (index !== -1) {
          messages.value.splice(index, 1);
        }
      }
      messageOptions.value = { message: null, x: 0, y: 0 };
    };

    const replyToMessage = (message) => {
      replyTo.value = message;
      newMessage.value = `@${message.senderUsername} `;
      messageOptions.value = { message: null, x: 0, y: 0 };
    };

    // Fonctions de formatage de texte
    const toggleBold = () => {
      textFormatting.value.bold = !textFormatting.value.bold;
      applyMarkdownFormatting('**', '**');
    };

    const toggleItalic = () => {
      textFormatting.value.italic = !textFormatting.value.italic;
      applyMarkdownFormatting('*', '*');
    };


    const applyMarkdownFormatting = (prefix, suffix) => {
      const textarea = document.querySelector('textarea');
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = newMessage.value.substring(start, end);
        
        if (selectedText) {
          // Si du texte est sélectionné, l'entourer avec les marqueurs
          const newText = newMessage.value.substring(0, start) + 
                         prefix + selectedText + suffix + 
                         newMessage.value.substring(end);
          newMessage.value = newText;
          
          // Repositionner le curseur
          setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + prefix.length, end + prefix.length);
          }, 0);
        } else {
          // Si aucun texte sélectionné, insérer les marqueurs à la position du curseur
          const newText = newMessage.value.substring(0, start) + 
                         prefix + suffix + 
                         newMessage.value.substring(end);
          newMessage.value = newText;
          
          // Repositionner le curseur entre les marqueurs
          setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + prefix.length, start + prefix.length);
          }, 0);
        }
      }
    };


    const formatMessageContent = (content) => {
      if (!content) return '';
      
      // Échapper les caractères HTML pour éviter les injections
      let formatted = content
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      
      // Appliquer le formatage Markdown
      formatted = formatted
        .replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold">$1</strong>') // **texte** -> <strong>texte</strong>
        .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>') // *texte* -> <em>texte</em>
        .replace(/\n/g, '<br>'); // Retours à la ligne
      
      return formatted;
    };

    // Utilitaires
    const formatDate = (dateString) => {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 1) return 'Aujourd\'hui';
      if (diffDays === 2) return 'Hier';
      if (diffDays <= 7) return `${diffDays - 1}j`;
      return date.toLocaleDateString('fr-FR');
    };

    const formatTime = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    };

    // Recharger les conversations périodiquement
    let conversationInterval = null;
    
    const startConversationPolling = () => {
      if (conversationInterval) {
        clearInterval(conversationInterval);
      }
      conversationInterval = setInterval(() => {
        loadConversations();
      }, 10000); // Recharger toutes les 10 secondes
    };
    
    const stopConversationPolling = () => {
      if (conversationInterval) {
        clearInterval(conversationInterval);
        conversationInterval = null;
      }
    };

    // Charger les données au montage
    onMounted(() => {
      loadConversations();
      loadAvailableUsers();
      startConversationPolling();
      
    });
    
    // Nettoyer l'intervalle au démontage
    onUnmounted(() => {
      stopConversationPolling();
    });

    return {
      currentUser,
      conversations,
      messages,
      selectedConversation,
      newMessage,
      searchUser,
      searchResults,
      showNewMessageModal,
      newMessageReceiver,
      newMessageContent,
      loading,
      loadingUsers,
      availableUsers,
      validUsers,
      getOtherUsername,
      loadConversations,
      loadAvailableUsers,
      searchUsers,
      selectConversation,
      startConversation,
      sendMessage,
      sendNewMessage,
      openNewMessageModal,
      markConversationAsRead,
      formatDate,
      formatTime,
      // Nouvelles fonctionnalités
      showEmojiPicker,
      isTyping,
      typingUser,
      showImageModal,
      selectedImage,
      showReactionModal,
      selectedMessageForReaction,
      messageOptions,
      replyTo,
      popularEmojis,
      reactionEmojis,
      toggleEmojiPicker,
      addEmoji,
      handleTyping,
      handleKeyDown,
      handleTextareaKeyDown,
      openImageModal,
      closeImageModal,
      showReactionPicker,
      closeReactionModal,
      addReaction,
      toggleReaction,
      showMessageOptions,
      copyMessage,
      deleteMessage,
      replyToMessage,
      // Formatage de texte
      textFormatting,
      toggleBold,
      toggleItalic,
      formatMessageContent,
      autoResizeTextarea
    };
  }
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Amélioration du scrollbar pour la zone de messages */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: rgba(180, 83, 9, 0.1);
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: rgba(180, 83, 9, 0.3);
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: rgba(180, 83, 9, 0.5);
}

/* Animation pour les messages */
.message-enter-active {
  transition: all 0.3s ease;
}

.message-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

/* Amélioration des transitions */
.transition-all {
  transition: all 0.2s ease;
}

/* Styles pour les emojis */
.emoji-picker {
  animation: slideUp 0.2s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Styles pour les réactions */
.reaction-bubble {
  transition: all 0.2s ease;
}

.reaction-bubble:hover {
  transform: scale(1.1);
  background-color: rgba(180, 83, 9, 0.6);
}

/* Styles pour le formatage de texte */
.formatting-button {
  transition: all 0.2s ease;
}

.formatting-button:hover {
  transform: scale(1.05);
}

.formatting-button.active {
  box-shadow: 0 0 0 2px rgba(180, 83, 9, 0.5);
}

/* Styles pour le textarea */
textarea {
  font-family: inherit;
  line-height: 1.5;
  transition: height 0.2s ease, font-size 0.2s ease;
  font-size: 14px; /* Taille par défaut */
}

textarea:focus {
  outline: none;
}

/* Styles pour le formatage des messages */
.font-bold {
  font-weight: bold;
}

.italic {
  font-style: italic;
}

/* Amélioration du placeholder */
textarea::placeholder {
  color: rgba(251, 191, 36, 0.6);
  font-style: italic;
}

/* Animation pour l'indicateur de frappe */
@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
}

.animate-bounce {
  animation: bounce 1s infinite;
}

/* Styles pour le menu contextuel */
.context-menu {
  animation: fadeIn 0.15s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Styles pour les modales */
.modal-backdrop {
  backdrop-filter: blur(4px);
}

/* Amélioration du scrollbar pour le sélecteur d'emojis */
.emoji-grid::-webkit-scrollbar {
  width: 4px;
}

.emoji-grid::-webkit-scrollbar-track {
  background: rgba(180, 83, 9, 0.1);
  border-radius: 2px;
}

.emoji-grid::-webkit-scrollbar-thumb {
  background: rgba(180, 83, 9, 0.3);
  border-radius: 2px;
}

.emoji-grid::-webkit-scrollbar-thumb:hover {
  background: rgba(180, 83, 9, 0.5);
}
</style>